import type { ReactNode, SVGProps } from 'react';

/*
 * The game's icon set: 24×24 line icons drawn with one consistent stroke, so every panel, button
 * and badge speaks the same visual language. Colour comes from `currentColor`.
 */

const paths = {
  // Tools
  select: <path d="M5 3l12 8-5.5 1.5L9 18z" />,
  land: <path d="M3 7l6-3 6 3 6-3v13l-6 3-6-3-6 3zM9 4v13M15 7v13" />,
  build: <path d="M4 20V9l8-5 8 5v11M9 20v-6h6v6M2 20h20" />,
  road: <path d="M8 3L5 21M16 3l3 18M12 4v3M12 10v3M12 16v3" />,
  taxiway: <path d="M3 12h18M12 3v18M3 12c5 0 9-4 9-9" />,
  taxiLine: <path d="M4 20c4-1 6-4 7-8s3-7 9-8M4 20h3M17 4h3M9 15l2 2M12 9l2 2" />,
  service: <path d="M9 3L7 21M15 3l2 18M12 5v2M12 11v2M12 17v2M3 8h3M18 16h3" />,
  junction: (
    <path d="M3 9h18M3 15h18M9 3v18M15 3v18M19 5v4M19 5h1.5a1 1 0 011 1v2a1 1 0 01-1 1H19" />
  ),
  toll: <path d="M2 5h20v3H2zM5 8v12M12 8v12M19 8v12M2 20h20M7 12l3-2M14 12l3-2" />,
  bulldoze: (
    <path d="M3 17h11l3-5h4v5M5 17v-4h7v4M14 12l-3-5H6M5 21a2 2 0 100-4 2 2 0 000 4zM14 21a2 2 0 100-4 2 2 0 000 4z" />
  ),
  // Buildings
  tower: <path d="M9 21l1-10h4l1 10M7 11h10l-1-4H8zM10 7V4h4v3M8 21h8M12 4V2" />,
  terminal: <path d="M2 20h20M4 20V10l8-4 8 4v10M8 20v-5h8v5M7 12h2M11 12h2M15 12h2" />,
  runway: <path d="M7 2L3 22M17 2l4 20M12 4v3M12 10v4M12 17v3" />,
  stand: <path d="M4 4h16v16H4zM12 4v9M8 16h8M12 13l-3 3M12 13l3 3" />,
  parking: <path d="M5 3h14v18H5zM10 17V8h3a2.5 2.5 0 010 5h-3" />,
  fuel: (
    <path d="M5 21V5a2 2 0 012-2h6a2 2 0 012 2v16M3 21h14M8 8h4M15 9l3 2v6a1.5 1.5 0 003 0V8l-3-3" />
  ),
  fire: (
    <path d="M12 22c4 0 7-2.7 7-6.5 0-3.5-2.5-5.5-4-8-1 2-2 3-3.5 3.5C12 8 11 5 9 2c-.5 4-4 6.5-4 12 0 4.5 3 8 7 8z" />
  ),
  hangar: <path d="M2 20h20M3 20v-7a9 9 0 0118 0v7M7 20v-6h10v6" />,
  handling: (
    <path d="M2 16V8h10v8M12 11h5l3 3v2h-8M5 19a2 2 0 100-4 2 2 0 000 4zM16 19a2 2 0 100-4 2 2 0 000 4z" />
  ),
  // HUD
  money: (
    <path d="M12 2v20M17 6.5C17 4.6 14.8 3.5 12 3.5S7 4.6 7 6.7c0 4.6 10 2.4 10 7.4 0 2.2-2.3 3.4-5 3.4s-5.2-1.2-5.2-3.4" />
  ),
  clock: <path d="M12 21a9 9 0 100-18 9 9 0 000 18zM12 7v5l3 2" />,
  pause: <path d="M8 5v14M16 5v14" />,
  play: <path d="M7 4l13 8-13 8z" />,
  fast: <path d="M3 5l9 7-9 7zM12 5l9 7-9 7z" />,
  faster: <path d="M2 6l6 6-6 6zM9 6l6 6-6 6zM16 6l6 6-6 6z" />,
  plane: (
    <path d="M10.5 21l1.5-1 1.5 1v-2l-1.2-1V14l7.7 3v-2l-7.7-5V4.5C12.3 3.6 12.2 3 12 3s-.3.6-.3 1.5V10L4 15v2l7.7-3v4L10.5 19z" />
  ),
  trophy: (
    <path d="M8 4h8v5a4 4 0 0 1-8 0zM8 6H5a3 3 0 0 0 3 4M16 6h3a3 3 0 0 1-3 4M12 13v4M8 20h8M10 17h4" />
  ),
  refresh: <path d="M20 11a8 8 0 1 0-2.3 5.7M20 4v7h-7" />,
  rotate: <path d="M20 12a8 8 0 1 1-2.6-5.9M20 3.5V8h-4.5" />,
  doc: <path d="M6 3h8l4 4v14H6zM14 3v4h4M9 12h6M9 16h6" />,
  area: <path d="M4 4h4M4 4v4M20 4h-4M20 4v4M4 20h4M4 20v-4M20 20h-4M20 20v-4M9 9h6v6H9z" />,
  crown: <path d="M4 18h16M5 15l-1-8 5 4 3-6 3 6 5-4-1 8z" />,
  medal: <path d="M8 3l2 6M16 3l-2 6M12 21a6 6 0 1 0 0-12 6 6 0 0 0 0 12zM12 12.5v5" />,
  star: <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" />,
  crew: <path d="M12 11a4 4 0 100-8 4 4 0 000 8zM4 21c0-4 3.6-7 8-7s8 3 8 7M8 6h8" />,
  gear: (
    <path d="M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" />
  ),
  close: <path d="M6 6l12 12M18 6L6 18" />,
  train: (
    <path d="M6 3h12a2 2 0 0 1 2 2v9a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V5a2 2 0 0 1 2-2zM4 10h16M8 21l2-4M16 21l-2-4M8 14h.01M16 14h.01" />
  ),
  bird: (
    <path d="M3 12c4-1 6-4 9-4 2 0 3 1 4 2l5-1-3 3c0 4-4 7-9 7H5l3-3c-3-1-5-2-5-4zM14 10h.01" />
  ),
  cutlery: <path d="M7 3v8M5 3v5a2 2 0 0 0 4 0V3M7 11v10M17 3c-2 2-2 6 0 8v10" />,
  bed: <path d="M3 18V7M3 13h18v5M21 13a3 3 0 0 0-3-3h-7v3M7 11.5a1.5 1.5 0 1 0 0-.01" />,
  box: <path d="M3 7l9-4 9 4v10l-9 4-9-4zM3 7l9 4 9-4M12 11v10" />,
  moon: <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z" />,
  sound: <path d="M4 9h4l5-4v14l-5-4H4zM16.5 8.5a5 5 0 010 7M19 6a8.5 8.5 0 010 12" />,
  chat: <path d="M4 5h16v11H9l-5 4zM8 9h8M8 12h5" />,
  bus: (
    <path d="M5 17V5a2 2 0 012-2h10a2 2 0 012 2v12M5 17h14M5 11h14M8 21v-4M16 21v-4M8 14h.01M16 14h.01" />
  ),
  dice: (
    <path d="M5 4h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1zM8.5 8.5h.01M15.5 8.5h.01M12 12h.01M8.5 15.5h.01M15.5 15.5h.01" />
  ),
  edit: <path d="M4 20h4L19 9l-4-4L4 16zM13 7l4 4" />,
  save: <path d="M5 3h11l3 3v15H5zM8 3v5h7V3M8 21v-7h8v7" />,
  download: <path d="M12 3v12M7 10l5 5 5-5M4 21h16" />,
  upload: <path d="M12 16V4M7 9l5-5 5 5M4 21h16" />,
  trash: <path d="M4 7h16M9 7V4h6v3M6 7l1 14h10l1-14M10 11v6M14 11v6" />,
  check: <path d="M4 12l5 5L20 6" />,
  chevron: <path d="M9 6l6 6-6 6" />,
  back: <path d="M15 6l-6 6 6 6" />,
  lock: <path d="M6 11h12v10H6zM8 11V8a4 4 0 018 0v3" />,
  move: (
    <path d="M12 3v18M3 12h18M12 3l-3 3M12 3l3 3M12 21l-3-3M12 21l3-3M3 12l3-3M3 12l3 3M21 12l-3-3M21 12l-3 3" />
  ),
  warning: <path d="M12 3l10 18H2zM12 10v5M12 18v.5" />,
  info: <path d="M12 21a9 9 0 100-18 9 9 0 000 18zM12 11v6M12 7.5v.5" />,
  users: (
    <path d="M9 11a4 4 0 100-8 4 4 0 000 8zM2 21c0-3.9 3.1-7 7-7s7 3.1 7 7M16 3.2a4 4 0 010 7.6M18 14.5c2.4.9 4 3.2 4 6.5" />
  ),
  link: (
    <path d="M10 14a5 5 0 007 0l3-3a5 5 0 00-7-7l-1 1M14 10a5 5 0 00-7 0l-3 3a5 5 0 007 7l1-1" />
  ),
  globe: (
    <path d="M12 21a9 9 0 100-18 9 9 0 000 18zM3 12h18M12 3c2.5 2.5 3.8 5.5 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-5.5-3.8-9S9.5 5.5 12 3z" />
  ),
  monitor: <path d="M3 4h18v12H3zM8 20h8M12 16v4" />,
  phone: <path d="M8 3h8a1 1 0 011 1v16a1 1 0 01-1 1H8a1 1 0 01-1-1V4a1 1 0 011-1zM11 18h2" />,
  tablet: <path d="M5 3h14a1 1 0 011 1v16a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1zM11 18h2" />,
  exit: <path d="M15 4h4v16h-4M10 8l-4 4 4 4M6 12h11" />,
  grid: <path d="M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z" />,
  upgrade: <path d="M12 19V5M5 12l7-7 7 7" />,
  wind: <path d="M3 8h11a3 3 0 10-3-3M3 12h16a3 3 0 11-3 3M3 16h8" />,
  camera: <path d="M3 7h12v10H3zM15 10l6-3v10l-6-3M6 10h3" />,
  photo: <path d="M3 7h4l2-3h6l2 3h4v13H3zM12 17a4 4 0 100-8 4 4 0 000 8z" />,
  // Weather
  sun: (
    <path d="M12 17a5 5 0 100-10 5 5 0 000 10zM12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
  ),
  cloud: <path d="M7 19a5 5 0 01-.9-9.9A6 6 0 0117.7 8.1 4.5 4.5 0 0117.5 19z" />,
  rain: (
    <path d="M7 15a5 5 0 01-.9-9.9 6 6 0 0111.6-1A4.5 4.5 0 0117.5 15zM8 18l-1 3M12 18l-1 3M16 18l-1 3" />
  ),
  fog: <path d="M7 12a5 5 0 01-.9-9.9 6 6 0 0111.6-1A4.5 4.5 0 0117.5 12zM3 16h18M5 20h14" />,
  snow: <path d="M12 2v20M3.3 7l17.4 10M3.3 17L20.7 7M9 3.5l3 2 3-2M9 20.5l3-2 3 2" />,
  mute: <path d="M4 5h16v11H9l-5 4zM4 4l16 16" />,
  flag: <path d="M5 21V4M5 4h12l-2 4 2 4H5" />,
} satisfies Record<string, ReactNode>;

export type IconName = keyof typeof paths;

export function Icon({
  name,
  size = 20,
  ...rest
}: { name: IconName; size?: number } & Omit<SVGProps<SVGSVGElement>, 'name'>) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
      {...rest}
    >
      {paths[name]}
    </svg>
  );
}

/** Icon per building key (BuildingDef.key). */
export const BUILDING_ICONS: Record<string, IconName> = {
  tower: 'tower',
  terminal: 'terminal',
  runway: 'runway',
  stand: 'stand',
  parking: 'parking',
  fuelDepot: 'fuel',
  fireStation: 'fire',
  hangar: 'hangar',
  groundHandling: 'handling',
  grantSign: 'info',
  transitHub: 'bus',
  cargoTerminal: 'box',
  hotel: 'bed',
  railStation: 'train',
  constructionOffice: 'build',
  cateringKitchen: 'cutlery',
  wildlifeControl: 'bird',
  militaryGate: 'lock',
  shelter: 'hangar',
  militaryApron: 'land',
  opsCenter: 'tower',
  munitions: 'box',
  airDefence: 'globe',
  deicePad: 'wind',
  largeDeicePad: 'wind',
  directorate: 'doc',
  cafe: 'cutlery',
  restaurant: 'cutlery',
  mall: 'box',
  fuelStation: 'fuel',
  officePark: 'terminal',
  cityPark: 'bird',
  remoteParking: 'stand',
  contactStand: 'stand',
  helipad: 'plane',
  spotterDeck: 'camera',
  smallRunway: 'runway',
  largeRunway: 'runway',
  smallStand: 'stand',
  largeStand: 'stand',
  smallParking: 'stand',
  largeParking: 'stand',
  militaryTower: 'tower',
};

/** Icon per sky condition (Sky id). */
export const SKY_ICONS: IconName[] = ['sun', 'cloud', 'rain', 'fog', 'snow'];

/**
 * The game's emblem: a runway in perspective with a climbing aircraft, inside a rounded badge.
 * Used on the title screen, the loading screen and the top bar.
 */
export function Emblem({ size = 56 }: { size?: number }) {
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden focusable="false">
      <defs>
        <linearGradient id="emblem-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffd75a" />
          <stop offset="1" stopColor="#f2a900" />
        </linearGradient>
      </defs>
      <rect x="2" y="2" width="60" height="60" rx="14" fill="url(#emblem-bg)" />
      <path d="M22 58L29 30h6l7 28z" fill="#1b1600" opacity="0.88" />
      <path d="M32 33v4M32 41v5M32 50v6" stroke="#ffd75a" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M44.5 9.5c1.2-1.2 3-1 3.6-.4s.8 2.4-.4 3.6l-5 5 2.6 11.6-2.4 2.4-5.2-9.4-5 5 .6 4-1.8 1.8-2.4-4.6-4.6-2.4 1.8-1.8 4 .6 5-5-9.4-5.2 2.4-2.4 11.6 2.6z"
        fill="#1b1600"
      />
    </svg>
  );
}
